import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Mail, Lock, ArrowLeft, Smartphone } from 'lucide-react';
import api from '../../utils/api';

const ForgotPassword = () => {
  // 1 = email, 2 = otp + password, 3 = success
  const [step, setStep] = useState(1);

  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [maskedPhone, setMaskedPhone] = useState('****');

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  /* ---------------------------------------------------
     STEP 1: SEND OTP
  --------------------------------------------------- */
  const handleSendOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const res = await api.post('/auth/send-reset-otp', {
        email,
        verificationType: 'password_reset',
      });

      if (res.data?.success) {
        setMaskedPhone(res.data.maskedPhone || '****');
        setSuccessMessage(res.data.message || 'OTP sent successfully');
        setStep(2);
      } else {
        setError('Unable to send OTP');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------
     STEP 2: RESET PASSWORD (OTP VERIFIED HERE ONLY)
  --------------------------------------------------- */
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (otp.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await api.post('/auth/reset-password', {
        email,
        otp,
        newPassword,
      });

      setStep(3);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid or expired OTP');
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------
     RESEND OTP
  --------------------------------------------------- */
  const handleResendOTP = async () => {
    setLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      const res = await api.post('/auth/send-reset-otp', {
        email,
        verificationType: 'password_reset',
      });

      setSuccessMessage(res.data?.message || 'OTP resent successfully');
    } catch {
      setError('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <img src="/logo.png" alt="Tikhat Foods" className="h-16 object-contain" />
        </div>

        <div className="bg-white rounded-lg shadow-md p-8">

          {/* ---------------- STEP 1 ---------------- */}
          {step === 1 && (
            <>
              <h2 className="text-2xl font-bold mb-2">Reset Password</h2>
              <p className="text-gray-600 mb-6">
                Enter your registered email to receive OTP
              </p>

              {error && <div className="text-red-600 mb-4">{error}</div>}

              <form onSubmit={handleSendOTP} className="space-y-4">
                <div>
                  <label className="block text-sm font-semibold mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border rounded-md"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-white py-3 rounded-md"
                >
                  {loading ? 'Sending OTP...' : 'Send OTP'}
                </button>
              </form>
            </>
          )}

          {/* ---------------- STEP 2 ---------------- */}
          {step === 2 && (
            <>
              <button
                onClick={() => setStep(1)}
                className="flex items-center text-primary mb-4 text-sm"
              >
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </button>

              <h2 className="text-2xl font-bold mb-2">Set New Password</h2>

              <div className="bg-blue-50 border rounded-md p-4 mb-4">
                <div className="flex items-center text-sm mb-1">
                  <Mail className="w-4 h-4 mr-2" />
                  {email}
                </div>
                <div className="flex items-center text-sm">
                  <Smartphone className="w-4 h-4 mr-2" />
                  +91 {maskedPhone}
                </div>
              </div>

              {error && <div className="text-red-600 mb-3">{error}</div>}
              {successMessage && (
                <div className="text-green-600 mb-3">{successMessage}</div>
              )}

              <form onSubmit={handleResetPassword} className="space-y-4">
                <input
                  type="text"
                  maxLength={6}
                  value={otp}
                  onChange={(e) =>
                    setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))
                  }
                  placeholder="Enter 6-digit OTP"
                  className="w-full px-4 py-3 border rounded-md text-center text-xl tracking-widest"
                  required
                />

                <input
                  type="password"
                  placeholder="New Password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-3 border rounded-md"
                  required
                />

                <input
                  type="password"
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-3 border rounded-md"
                  required
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-white py-3 rounded-md"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>

                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={loading}
                  className="w-full text-sm text-primary mt-2"
                >
                  Resend OTP
                </button>
              </form>
            </>
          )}

          {/* ---------------- STEP 3 ---------------- */}
          {step === 3 && (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">
                Password Reset Successful
              </h3>
              <p className="text-gray-600 mb-6">
                You can now login using your new password
              </p>
              <Link
                to="/login"
                className="inline-block bg-primary text-white px-6 py-3 rounded-md"
              >
                Go to Login
              </Link>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
